/**
 * Unified analytics — GA4, Meta Pixel, Microsoft Clarity.
 * All three are injected globally by TrackingHead.
 */
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
    fbq?: (...args: unknown[]) => void;
  }
}

// ─── Low-level fire helpers ───────────────────────────────────────────────────

function fireGA(eventName: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined') {
    window.gtag?.('event', eventName, params);
  }
}

function fireMeta(eventName: string, params?: Record<string, unknown>) {
  if (typeof window !== 'undefined') {
    window.fbq?.('track', eventName, params);
  }
}

function fireClarity(eventName: string) {
  if (typeof window !== 'undefined') {
    window.clarity?.('event', eventName);
  }
}

// ─── Item type for ecommerce events ──────────────────────────────────────────

export interface TrackItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

// ─── Spec events ─────────────────────────────────────────────────────────────

/** Page 1 — Landing Page: initial load */
export function trackLandingPage() {
  const urlParams = new URLSearchParams(window.location.search);
  fireGA('landing_page', {
    page_title: document.title,
    page_location: window.location.href,
    utm_source: urlParams.get('utm_source') ?? '',
    utm_campaign: urlParams.get('utm_campaign') ?? '',
  });
  fireMeta('PageView');
  fireClarity('landing_page');
}

/** Page 1 — Landing Page: any CTA button click */
export function trackLandingPageCta(position: 'hero_banner' | 'bottom_banner' | 'header') {
  fireGA('landing_page_cta', { cta_position: position });
  fireMeta('Lead', { cta_position: position });
  fireClarity('landing_page_cta');
}

/** Page 2 (Step 1) — Occasion & Build: page load */
export function trackViewStep1() {
  fireGA('view_step1', {
    page_title: document.title,
    page_location: window.location.href,
    step_number: 1,
  });
  fireMeta('PageView');
  fireClarity('view_step1');
}

/** Page 2 (Step 1) — Occasion & Build: continue button */
export function trackSubmitStep1(occasion: string, buildType: string) {
  fireGA('submit_step1', { step_number: 1, occasion, build_type: buildType });
  fireClarity('submit_step1');
}

/** Page 3 (Step 2) — Photo & Body Details: page load */
export function trackViewStep2() {
  fireGA('view_step2', {
    page_title: document.title,
    page_location: window.location.href,
    step_number: 2,
  });
  fireMeta('PageView');
  fireClarity('view_step2');
}

/** Page 3 (Step 2) — Photo & Body Details: "Analyse my style" click. No PII. */
export function trackSubmitStep2(params: {
  heightFt: string;
  heightIn: string;
  weightKg: string;
  photoUploaded: boolean;
}) {
  fireGA('submit_step2', {
    step_number: 2,
    height_ft: params.heightFt,
    height_in: params.heightIn,
    weight_kg: params.weightKg,
    photo_uploaded: params.photoUploaded,
  });
  fireMeta('Lead');
  fireClarity('submit_step2');
}

/** Page 5 (Step 3) — Choose Your Look: page load */
export function trackViewStep3() {
  fireGA('view_step3', {
    page_title: document.title,
    page_location: window.location.href,
    step_number: 3,
  });
  fireMeta('PageView');
  fireClarity('view_step3');
}

/** Page 5 (Step 3) — Choose Your Look: user selects one of the 3 looks */
export function trackStep3Select(lookNumber: 1 | 2 | 3) {
  fireGA(`step3_select_${lookNumber}`, { step_number: 3 });
  fireClarity(`step3_select_${lookNumber}`);
}

/** Page 5 (Step 3) — Your Details: "Preview this look on me" click */
export function trackSubmitStep3() {
  fireGA('submit_step3', { step_number: 3 });
  fireMeta('Lead');
  fireClarity('submit_step3');
}

/** Page 4 (Step 3) — AI processing screen: page load */
export function trackViewAnalysing() {
  fireGA('view_analysing', {
    page_title: document.title,
    page_location: window.location.href,
    step_number: 3,
  });
  fireMeta('PageView');
  fireClarity('view_analysing');
}

/** Page 5 (Step 4) — Complete Look: look impression on page load */
export function trackCompleteLook(params: { items: TrackItem[]; value: number }) {
  fireGA('complete_look', {
    item_list_name: 'AI Curated Look',
    currency: 'INR',
    value: params.value,
    items: params.items,
  });
  fireMeta('ViewContent', {
    currency: 'INR',
    value: params.value,
    contents: params.items.map(i => ({ id: i.item_id, quantity: 1 })),
    content_type: 'product',
  });
  fireClarity('complete_look');
}

/** Page 5 (Step 4) — Complete Look: "Buy my look" CTA click */
export function trackBeginCheckout(params: {
  value: number;
  coupon: string;
  items: TrackItem[];
  currency?: string;
}) {
  fireGA('begin_checkout', {
    currency: 'INR',
    value: params.value,
    coupon: params.coupon,
    items: params.items,
  });
  fireMeta('InitiateCheckout', {
    currency: 'INR',
    value: params.value,
    num_items: params.items.length,
    content_ids: params.items.map(i => i.item_id),
  });
  fireClarity('begin_checkout');
}

// ─── Legacy stubs (old-flow archived files only — do not use in new flow) ────

/** @deprecated */
export function trackGAEvent(_eventName: string, _params?: Record<string, string | number | boolean>) {}
/** @deprecated */
export function trackClarityEvent(_eventName: string, _params?: Record<string, string | number | boolean>) {}
